// tenant/OrderPages/TenantOrderDetailPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import { API } from 'aws-amplify';

import { useTenantUserInfo } from '../../context/userInfoProvider';
import { updateOrder } from '../../graphql/mutations';

type RouteParams = {
  id: string;
};

const pageContainer: React.CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#261136',
  padding: 24,
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const shellCard: React.CSSProperties = {
  width: '100%',
  maxWidth: 700,
  background:
    'radial-gradient(circle at top, #3A1F52 0, #170824 45%, #12061f 100%)',
  borderRadius: 16,
  padding: 24,
  boxSizing: 'border-box',
  boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
  color: '#F5F5FA',
};

const headerRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 16,
  gap: 8,
};

const titleStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 600,
};

const backButton: React.CSSProperties = {
  fontSize: 13,
  padding: '6px 12px',
  borderRadius: 999,
  border: '1px solid rgba(255,255,255,0.3)',
  background: 'transparent',
  color: '#F5F5FA',
  cursor: 'pointer',
};

const primaryButton: React.CSSProperties = {
  fontSize: 13,
  padding: '6px 14px',
  borderRadius: 999,
  border: 'none',
  background: '#7B1FA2',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
};

const sectionCard: React.CSSProperties = {
  backgroundColor: 'rgba(23, 8, 36, 0.9)',
  borderRadius: 12,
  padding: 16,
  border: '1px solid rgba(255,255,255,0.05)',
  marginBottom: 10,
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.7,
  opacity: 0.7,
  marginBottom: 3,
};

const valueStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 500,
};

const emptyText: React.CSSProperties = {
  fontSize: 14,
  opacity: 0.8,
};

const badge: React.CSSProperties = {
  fontSize: 11,
  padding: '2px 8px',
  borderRadius: 999,
  textTransform: 'uppercase',
  letterSpacing: 0.7,
  backgroundColor: '#312047',
  color: '#C7B6FF',
  marginLeft: 8,
};

const fieldInput: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.18)',
  background: 'rgba(255,255,255,0.06)',
  color: '#F5F5FA',
  fontSize: 14,
  fontFamily: 'inherit',
  outline: 'none',
};

const TenantOrderDetailPage: React.FC = () => {
  const { id } = useParams<RouteParams>();
  const history = useHistory();
  const location = useLocation();
  const {
    dataSourcePastOrders,
    dataSourceNewOrders,
    loading,
    fetchData,
  } = useTenantUserInfo();

  const order = useMemo(() => {
    const allOrders = [
      ...(dataSourceNewOrders || []),
      ...(dataSourcePastOrders || []),
    ];
    return allOrders.find((o: any) => o.id === id);
  }, [id, dataSourceNewOrders, dataSourcePastOrders]);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  // "Parking instructions" maps to the order's address.specialInstructions field.
  const [parkingInstructions, setParkingInstructions] = useState('');
  // The items text field; on save it replaces orderItems[0] (no schema change).
  const [itemsText, setItemsText] = useState('');

  // Sync editable fields whenever the order loads/changes.
  useEffect(() => {
    if (!order) return;
    const addr = order.address && typeof order.address === 'object' ? order.address : null;
    setParkingInstructions(addr?.specialInstructions ?? '');
    setItemsText(
      Array.isArray(order.orderItems)
        ? (order.orderItems[0] ?? '')
        : (order.orderItems ?? ''),
    );
  }, [order]);

  // Deep-link from the reminder email: ?edit=1 drops the user straight into
  // edit mode (skip the click on "Update") for any editable order.
  useEffect(() => {
    if (!order) return;
    const editable = ['PENDING', 'INCOMPLETE'].includes(String(order.status || '').toUpperCase());
    if (!editable) return;
    if (new URLSearchParams(location.search).get('edit') === '1') {
      setEditing(true);
    }
  }, [order, location.search]);

  // Still fetching the tenant's data — don't flash "not found" during the load.
  if (loading) {
    return (
      <div style={pageContainer}>
        <div style={shellCard}>
          <p style={emptyText}>Loading your order…</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div style={pageContainer}>
        <div style={shellCard}>
          <div style={headerRow}>
            <h1 style={titleStyle}>Order not found</h1>
            <button style={backButton} onClick={() => history.push('/tenant/home')}>
              ← Back home
            </button>
          </div>
          <p style={emptyText}>
            We couldn&apos;t find that order. It may have been removed or you
            may have opened an outdated link.
          </p>
        </div>
      </div>
    );
  }

  const addressObj =
    order.address && typeof order.address === 'object' ? order.address : null;

  const dateLabel = order.date
    ? moment(order.date).format('MMMM DD, YYYY')
    : 'Unknown date';
  const timeLabel = order.time || 'Unknown time';
  const jobType = order.jobType || 'Order';
  const status = order.status || '';
  const items =
    Array.isArray(order.orderItems) && order.orderItems.length
      ? order.orderItems.join(', ')
      : 'No specific items listed';
  const movingTime =
    typeof order.movingTime === 'number'
      ? `${order.movingTime} min`
      : order.movingTime || 'Not recorded';
  const movingFee =
    typeof order.movingFee === 'number'
      ? `$${order.movingFee}`
      : order.movingFee || 'Not recorded';

  // The customer can update an order until the move happens.
  const canEdit = ['PENDING', 'INCOMPLETE'].includes(String(status).toUpperCase());

  const handleCancel = () => {
    setParkingInstructions(addressObj?.specialInstructions ?? '');
    setItemsText(
      Array.isArray(order.orderItems)
        ? (order.orderItems[0] ?? '')
        : (order.orderItems ?? ''),
    );
    setEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const currentItems = Array.isArray(order.orderItems) ? order.orderItems : [];
      // Replace the first item with the edited text; keep any others untouched.
      const newOrderItems = currentItems.length > 0
        ? [itemsText, ...currentItems.slice(1)]
        : [itemsText];

      const input: any = {
        id: order.id,
        orderItems: newOrderItems,
      };
      if (addressObj) {
        input.address = {
          tenantID: addressObj.tenantID ?? order.tenantID ?? '',
          addressLine1: addressObj.addressLine1 ?? '',
          addressLine2: addressObj.addressLine2 ?? '',
          city: addressObj.city ?? '',
          state: addressObj.state ?? '',
          zip: addressObj.zip ?? '',
          building: addressObj.building ?? '',
          parking: addressObj.parking ?? '',
          specialInstructions: parkingInstructions.trim(),
        };
      }
      await API.graphql({ query: updateOrder, variables: { input } });
      // Refresh context data in place instead of a full page reload — a reload
      // re-mounts before the context refetches and briefly shows "not found".
      if (fetchData) await fetchData();
      setEditing(false);
      setSaving(false);
    } catch (e) {
      console.error('Failed to update order:', e);
      alert('Could not save your changes. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div style={pageContainer}>
      <div style={shellCard}>
        <div style={headerRow}>
          <div>
            <h1 style={titleStyle}>Order details</h1>
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              Order ID: <code>{id}</code>
              {status && <span style={badge}>{String(status).toLowerCase()}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {editing ? (
              <>
                <button style={backButton} onClick={handleCancel} disabled={saving}>
                  Cancel
                </button>
                <button style={primaryButton} onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </>
            ) : (
              <>
                {canEdit && (
                  <button style={primaryButton} onClick={() => setEditing(true)}>
                    Update
                  </button>
                )}
                <button style={backButton} onClick={() => history.goBack()}>
                  ← Back
                </button>
              </>
            )}
          </div>
        </div>

        {editing && (
          <p style={{ ...emptyText, marginTop: 0 }}>
            Update your parking instructions and your item list below, then save.
          </p>
        )}

        {/* Date / Time */}
        <section style={sectionCard}>
          <div style={{ marginBottom: 10 }}>
            <div style={labelStyle}>Date</div>
            <div style={valueStyle}>{dateLabel}</div>
          </div>
          <div>
            <div style={labelStyle}>Time slot</div>
            <div style={valueStyle}>{timeLabel}</div>
          </div>
        </section>

        {/* Type + Address */}
        <section style={sectionCard}>
          <div style={{ marginBottom: 10 }}>
            <div style={labelStyle}>Order type</div>
            <div style={valueStyle}>{jobType}</div>
          </div>

          {(order.moverOption === 'Basic' || order.moverOption === 'Itemized') && (
            <div style={{ marginBottom: 10 }}>
              <div style={labelStyle}>Valet tier</div>
              <div style={valueStyle}>{order.moverOption}</div>
            </div>
          )}

          {order.address && (
            <div>
              <div style={labelStyle}>Address</div>
              <div style={valueStyle}>
                {typeof order.address === 'string'
                  ? order.address
                  : [
                      order.address.addressLine1,
                      order.address.addressLine2,
                      order.address.city,
                      order.address.state,
                      order.address.zip,
                    ]
                      .filter(Boolean)
                      .join(', ')}
              </div>
            </div>
          )}
        </section>

        {/* Parking instructions */}
        {addressObj && (
          <section style={sectionCard}>
            <div style={labelStyle}>Parking instructions</div>
            {editing ? (
              <textarea
                style={{ ...fieldInput, resize: 'vertical' }}
                rows={3}
                value={parkingInstructions}
                placeholder="Where to park, gate codes, where to meet, anything our movers should know…"
                onChange={e => setParkingInstructions(e.target.value)}
              />
            ) : (
              <div style={valueStyle}>{addressObj.specialInstructions || '—'}</div>
            )}
          </section>
        )}

        {/* Items */}
        <section style={sectionCard}>
          <div style={labelStyle}>Items</div>
          {editing ? (
            <textarea
              style={{ ...fieldInput, resize: 'vertical' }}
              rows={5}
              value={itemsText}
              placeholder="List your items — sizes, counts, fragile pieces, anything that helps us plan."
              onChange={e => setItemsText(e.target.value)}
            />
          ) : (
            <div style={valueStyle}>{items}</div>
          )}
        </section>

        {/* Moving info (if present) */}
        {(order.movingTime || order.movingFee) && (
          <section style={sectionCard}>
            <div style={{ marginBottom: 10 }}>
              <div style={labelStyle}>Moving time</div>
              <div style={valueStyle}>{movingTime}</div>
            </div>
            <div>
              <div style={labelStyle}>Total moving fees</div>
              <div style={valueStyle}>{movingFee}</div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default TenantOrderDetailPage;
